import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ListResponse } from '@lib/api';
import type { Urun, CreateUrunInput, ListUrunQuery } from '@/types/urun';

const QUERY_KEY = 'urunler';

export function useUrunler(query: ListUrunQuery = {}) {
  return useQuery({
    queryKey: [QUERY_KEY, query],
    queryFn: () =>
      api
        .get('urunler', {
          searchParams: Object.entries(query).reduce((acc, [k, v]) => {
            if (v !== undefined && v !== '') acc[k] = String(v);
            return acc;
          }, {} as Record<string, string>),
        })
        .json<ListResponse<Urun>>(),
  });
}

export function useUrun(id: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () =>
      api.get(`urunler/${id}`).json<{ data: Urun }>().then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateUrun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUrunInput) =>
      api.post('urunler', { json: input }).json<{ data: Urun }>().then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}

export function useUpdateUrun(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CreateUrunInput>) =>
      api.patch(`urunler/${id}`, { json: input }).json<{ data: Urun }>().then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY, id] });
    },
  });
}

export function useDeleteUrun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`urunler/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}
